
import { Box, Button, Dialog, DialogContent, IconButton, InputBase, makeStyles, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@material-ui/core";
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import PencilIcon from '@material-ui/icons/Create';
import SearchIcon from '@material-ui/icons/Search';
import clsx from "clsx";
import { createRef, forwardRef, memo, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../hooks/useAlert";
import { Loading } from "../../parts/Loading";
import { useCustomerChartList } from "../../store/ChartSettingState";
import { DATA_HAS_BEEN_CHANGED_ERROR } from "../../utils/LinksAPI";
import RoleControl from "../RoleControl";
import { SUBJ_CHART_SETTING_LIST, VERB_UPDATE } from "../../utils/AccessControl";

const useStyles = makeStyles({
	container: {
		height: '100%',
		// border: '1px solid #a7a7a7',
	},
	hide: {
		display: 'none',
	},
	flexColumn: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	flexVariable: {
		overflow: 'hidden',
		flexGrow: 1,
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	fixedCell: {
		position: 'sticky',
		left: 0,
	},
	tableCell: {
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
		},
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 2,
		top: 0,
		'&:nth-child(-n+1)': {
			zIndex: 3
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	iconCell: {
		width: '32px',
		minWidth: '32px',
		maxWidth: '32px',
		verticalAlign: 'middle',
	},
	imageCell: {
		padding: '4px',
		width: '72px',
		boxSizing: 'border-box',
		'& img': {
			display: 'block',
			margin: '0 auto',
		}
	},
	checkCell: {
		verticalAlign: 'middle',
		transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
	},
	checkIcon: {
		width: 24,
		height: 24,
		margin: '0 auto',
		'& .MuiSvgIcon-root': {
			color: 'rgb(2, 136, 209)',
		}
	},
	newline: {
		whiteSpace: 'break-spaces',
	},
	alignRight: {
		textAlign: 'right',
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		color: 'rgba(0, 0, 0, 0.54)',
	},
	formTitle: {
		fontWeight: 'bold',
	},
	formBody: {
		marginTop: '8px',
		marginLeft: '16px',
	},
	formInputText: {
		'& .MuiFormLabel-root': {
			color: 'black',
			'&.Mui-error': {
				color: '#f44336',
			}
		},
		// '&:not(:first-child)': {
		// 	marginTop: '20px',
		// },
		'& .required': {
			color: '#f44336',
		},
		'& input[type=color]': {
			width: '50px',
			height: '30px',
		},
	},
	formInputLonLat: {
		maxWidth: '12em',
	},
	formRoot: {
		fontSize: '75%',
		'& .required': {
			color: '#f44336',
		},
		'& dl:not(:first-child)': {
			marginTop: '20px',
		},
		'& dt': {
			marginBottom: '5px',
		},
		'& #iconId-checkbox': {
			display: 'flex',
			minWidth: '10rem',
		},
	},
	formHorizontally: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'baseline',
	},
	formSpacer: {
		'& > *:not(:first-of-type)': { 
			marginLeft: '20px'
		},
	},
	formHeading: {
		marginRight: '10px',
	},
	menuItem: {
		paddingTop: 0,
		paddingBottom: 0,
	},
	menuItemCheckbox: {
		padding: 4,
	},
	menuItemText: {
		marginLeft: 4,
	},
});

const DEF_TYPES = {
	0: 'General',
	1: 'TC',
	2: 'VA',
};

const columns = [
	{ id: 'isEnable', label: 'Display' },
	{ id: 'chartName', label: 'Chart Name' },
	{ id: 'chartDescription', label: 'Description' },
	{ id: 'dataSource', label: 'Source' },
	{ id: 'chartType', label: 'Type', format: (v) => DEF_TYPES[v] },
];

const formInputList = [
	{
		code: 'isEnable',
		name: 'Display',
		type: 'switch',
		rules: {
			// required: true,
		},
		default: false,
	},
];

const formDefault = formInputList.reduce((a, v) => {
	if ("default" in v) {
		a[v.code] = v.default;
	} else {
		a[v.code] = "";
	}
	return a;
}, {});

const formInputItem = code => {
	return formInputList.find(elem => elem.code === code) || {
		code,
		rules: {}
	};
};

const ChartListTable = memo(forwardRef(({filterWord, handleEdit, setWaiting, tableRef}, ref) => {
	const {
		customerChartList,
		updateCustomerChart,
		refreshCustomerChartList
	} = useCustomerChartList();
	const { showError, showWarning } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const sortedCustomerChartList = useMemo(() => {
		// return Array.from(customerChartList).sort((a, b) => a.chartType.localeCompare(b.chartType) || a.chartId.localeCompare(b.chartId));
		return Array.from(customerChartList);
	}, [customerChartList]);

	const list = useMemo(() => {
		if (filterWord === "") {
			return sortedCustomerChartList;
		}
		return sortedCustomerChartList.filter(row => {
			return columns.some(col => {
				switch (col.id) {
					default:
						const cell = row[col.id] && ((col.format && col.format(row[col.id], t)) || row[col.id]);
						return cell !== undefined && cell !== null && String(cell).indexOf(filterWord) !== -1;
				}
			});
		});
	}, [sortedCustomerChartList, filterWord, t]);

	useEffect(() => {
		return () => {
			refreshCustomerChartList();
		}
	}, [refreshCustomerChartList]);

	useImperativeHandle(ref, () => ({
		update: (updateData) => {
			setWaiting(true);
			updateCustomerChart(updateData)
				.then(() => {
					// console.info("update comp");
				})
				.catch(e => {
					const name = t('Company Setting');
					if (e.response?.status === DATA_HAS_BEEN_CHANGED_ERROR) {
						console.warn("update failed", e);
						showWarning(`${t('validate_optimistic_lock_with_name', {name})}`, {timeout: 5000});
						refreshCustomerChartList();
					} else {
						console.error("update error", e);
						showError(`${t('update_failed_with_name', {name})}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		check: (item, value) => {
			return customerChartList.every(v => v[item] !== value);
		}
	}));

	return (
		<>
			<Box className={classes.flexColumn}>
				<Box className={classes.flexVariable}>
					<TableContainer className={classes.container} ref={tableRef}>
						<Table aria-label="sticky table" size="small" className={classes.stickyTable}>
							<TableHead>
								<TableRow className={classes.tableRow}>
									<TableCell
										key='edit'
										className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
										padding="none"
									>
									</TableCell>
									{columns.map((column, _i) => (
										column.label && <TableCell
											key={`th_${column.id}`}
											align={column.align}
											style={{ minWidth: 0 }}
											className={clsx(classes.headerCell, classes.tableCell)}
											{...column.colSpan ? {colSpan: column.colSpan} : ''}
										>
											{t(column.label)}
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{list.map((row) => {
									return (
										<TableRow hover role="checkbox" tabIndex={-1} key={row.chartId}>
											<TableCell key='editBtn' className={clsx(classes.tableCell, classes.iconCell)} padding="none" align="center">
												<RoleControl subject={SUBJ_CHART_SETTING_LIST} verb={VERB_UPDATE} >
													<IconButton aria-label="edit" size="small" className={classes.smallBtn} onClick={() => handleEdit(row)}>
														<Tooltip title="edit" arrow >
															<PencilIcon fontSize="small" />
														</Tooltip>
													</IconButton>
												</RoleControl>
											</TableCell>
											{columns.map((column, _i) => {
												const value = row[column.id];
												return (
													<TableCell
														key={`tb_${row.chartId}_${column.id}`}
														align={column.align}
														padding={['isEnable'].includes(column.id) ? 'checkbox' : 'normal'}
														className={clsx(classes.tableCell, ['isEnable'].includes(column.id) && classes.checkCell)}
													>
														{
															['isEnable'].includes(column.id) ?
																value ?
																	<Box className={clsx(classes.checkIcon)}>
																		<CheckIcon color="secondary" />
																	</Box>
																	:
																	<></>
																:
																column.format ? column.format(value, t) : value
														}
													</TableCell>
												);
											})}
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</Box>
		</>
	);
}));

function ChartList() {
	const tableRef = useRef(null);
	const childRef = useRef({});
	const inputFilterRef = useRef(null);
	const [formData, setFormData] = useState({});
	const [filterWord, setFileterWord] = useState('');
	const [open, setOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [waiting, setWaiting] = useState(false);

	const inputRefs = formInputList.reduce((a, v) => (a[v.code] = createRef()) && a, {});

	const { control, formState: { errors, isDirty, isValid }, getValues, reset } = useForm({
		mode: 'onChange',
	});

	const classes = useStyles();
	const {t} = useTranslation();

	const handleEdit = useCallback((row) => {
		setEditMode(true);
		const _formData = Object.assign({}, row, {
		});

		Object.keys(formDefault).forEach(key => (_formData[key] === null || _formData[key] === undefined) && (_formData[key] = formDefault[key]));

		reset(_formData);
		setFormData(_formData);
		setOpen(true);
	}, [reset]);

	const handleSearch = () => {
		const word = inputFilterRef.current.value;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleDeleteFilter = () => {
		const word = "";
		inputFilterRef.current.value = word;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleClose = () => {
		setOpen(false);
	}

	const handleSave = () => {
		const data = getValues();
		const common = [
			"chartId",
			"isEnable",
			"updateTime",
		];

		const _data = {};
		common.forEach(v => {
			if (v === 'type') {
				_data[v] = data[v] | 0;
			} else {
				_data[v] = data[v]
			}
		});

		childRef.current.update(_data);

		setOpen(false);
	}

	return (
		<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 50px 10px 10px' }}>
				<Paper component="form" className={classes.filterBox} onSubmit={(event) => { handleSearch(); event.preventDefault(); }}>
					<div className={classes.searchIcon}>
						<SearchIcon />
					</div>
					<InputBase
						className={classes.input}
						placeholder={`${t('Chart Name')}、${t('Description')}、${t('Source')}...`}
						inputProps={{ 'aria-label': 'filter chart list' }}
						inputRef={inputFilterRef}
						// onChange={handleChange}
					/>
					{filterWord.length > 0 && <IconButton type="button" className={classes.iconButton} aria-label="delete" onClick={handleDeleteFilter}>
						<CloseIcon />
					</IconButton>}
				</Paper>
			</Box>
			<Box style={{ padding: '0 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
				{waiting && <Loading backdrop={true} />}
				<Suspense fallback={<Loading />}>
					<ChartListTable filterWord={filterWord} ref={childRef} handleEdit={handleEdit} setWaiting={setWaiting} tableRef={tableRef}/>
				</Suspense>
			</Box>
			<Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
				<DialogContent>
					<Box style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row-reverse' }}>
						<Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', marginLeft: '15px' }}>
							<Box style={{ marginLeft: 'auto'}}>
								<IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
									<CloseIcon />
								</IconButton>
							</Box>
							<Box>
								<Button variant="outlined" size="small" className={classes.btn} onClick={handleSave} disabled={!(isDirty && isValid)}>{t('save')}</Button>
							</Box>
						</Box>
						<Box style={{ display: 'flex', flexDirection: 'column', paddingBottom: '40px' }}>
							<Box className={classes.formTitle}>{ formData.chartName }</Box>
							<Box className={classes.formBody}>
								<Box>{ formData.chartDescription }</Box>
								<ul className={classes.formRoot} style={{ marginTop: '20px' }}>
									<li>
										<dl className={clsx(classes.dataList)}>
											<dt className={classes.dataLabel}>{t('Display')}<span className="required"> * {t('required')}</span></dt>
											<dd style={{ display: 'flex', alignItems: 'baseline' }} className={classes.formSpacer}>
												<FormSwitch code="isEnable" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} />
											</dd>
										</dl>
									</li>
								</ul>
							</Box>
						</Box>
					</Box>
				</DialogContent>
			</Dialog>
		</Box>
	)
}

export default ChartList;

const makeRules = (item, t, {childRef, getValues, formData}) => {
	const result = {
		required: item.rules.required ? t('validate_required') : false,
	};
	if (item.rules.pattern) {
		result.pattern = {
			value: item.rules.pattern,
			message: t('validate_pattern')
		};
	}
	if (item.rules.maxLength) {
		result.maxLength = {
			value: item.rules.maxLength,
			message: t('validate_max_length', { maxLength: item.rules.maxLength }),
		};
	}
	result.validate = {
		common: value => {
			if (!item.rules.unique) {
				return true;
			}
			if (formData[item.code] === value) {
				return true;
			}
			return childRef.current.check(item.code, value) || t('already exists')
		},
		...Object.fromEntries(Object.entries({...item.rules.validate}).map(([key, func]) => {
			return [key, (value) => func(value, {getValues, t})];
		})),
	};

	return result;
};

const FormSwitch = ({code, inputRefs, childRef, control, getValues, formData, errors, editMode, ...prop}) => {

	// const classes = useStyles();
	const {t} = useTranslation();

	const item = formInputItem(code);
	const rules = makeRules(item, t, {childRef, getValues, formData});

	return (
		<Controller
			key={item.code}
			control={control}
			rules={rules}
			render={({ field: { name, onChange, onBlur, value } }) => (
				<Switch
					checked={value}
					onChange={onChange}
					color="primary"
					name={name}
					inputProps={{ 'aria-label': 'primary checkbox' }}
					inputRef={inputRefs[name]}
					{...prop}
				/>
			)}
			name={item.code}
		/>
	);
};
